import React, { ChangeEvent, useState } from "react";
import { makeStyles } from "@material-ui/core";
import classNames from "classnames";
import { Box } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import moment from "moment";
import { getLocale } from "../../services/Locale";
import { DATE_TIME_FORMAT } from "../../enums/date";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    "& input": {
      width: "100%",
      fontFamily: "inherit",
      fontWeight: "inherit",
    },
    paddingBottom: theme.spacing(2),
  },
  label: {
    fontSize: "0.775rem",
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },
  input: {
    width: "100%",
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5, 2.5),
    fontSize: "0.875rem", // text-sm
    "@media (max-width: 450px)": {
      marginTop: 0,
      padding: theme.spacing(1, 1.25),
      fontSize: "0.775rem !important",
    },
    borderRadius: theme.shape.borderRadius * 3,
  },
  error: {
    color: theme.palette.error.main,
  },
  borderError: {
    border: `1px solid ${theme.palette.error.main}`,
    "&:focus": {
      outline: "none",
      boxShadow: `0 0 0 2px ${theme.palette.error.main}`,
    },
  },
  borderNormal: {
    border: "1px solid #e5e7eb", // ~ border-gray-300
    "&:focus": {
      outline: "none",
      border: `1px solid ${theme.palette.primary.light}`,
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
      // ~ focus:ring-2 focus:ring-primary/20
    },
  },
}));

interface ICustomDateProps {
  name: string;
  label?: string;
  value?: Date;
  isSubmitted?: boolean;
  displayError?: boolean;
  errorMessageId?: string;
  type?: "date" | "datetime-local";
  handleDateChange: (name: string, date: any) => void;
  maxDate?: Date;
  minDate?: Date;
}

const CustomDate = (props: ICustomDateProps) => {
  const classes = useStyles();
  const {
    name,
    label,
    handleDateChange,
    value,
    type = "date",
    maxDate,
    minDate,
  } = props;

  const isSubmitted = !!props.isSubmitted;
  const displayError = !!props.displayError;
  const defaultErrorMessageId = props.errorMessageId || "error.notEmptyField";
  const locale = getLocale();
  moment.locale(locale);
  const [error, setError] = useState<string>("");

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    // Si aucune valeur n'est saisie, on passe null
    if (!value) {
      handleDateChange(name, null);
      return;
    }

    // Vérifier si la date est inférieure à la date minimale
    if (
      minDate &&
      new Date(value) <
        new Date(moment(minDate).format(DATE_TIME_FORMAT.DATE_ONLY))
    ) {
      setError("La date ne peut pas être inférieure à la date minimale");
      return;
    }

    // Vérifier si la date dépasse la date maximale
    if (
      maxDate &&
      new Date(value) >
        new Date(moment(maxDate).format(DATE_TIME_FORMAT.DATE_ONLY))
    ) {
      setError("La date ne peut pas dépasser la date maximale");
      return;
    }
    // Aucune erreur, on réinitialise le message d'erreur et on notifie le changement
    setError("");
    handleDateChange(name, moment(value).format(DATE_TIME_FORMAT.DATE_ONLY));
  };

  const hasError =
    (isSubmitted && !value) || (isSubmitted && displayError) || Boolean(error);

  return (
    <Box className={classes.root}>
      {label && (
        <span className={classNames(classes.label, hasError && classes.error)}>
          {label}
        </span>
      )}

      <input
        name={name}
        type={type}
        value={value ? moment(value).format(DATE_TIME_FORMAT.DATE_ONLY) : ""}
        onChange={handleChange}
        min={
          minDate
            ? moment(minDate).format(DATE_TIME_FORMAT.DATE_ONLY)
            : undefined
        }
        max={
          maxDate
            ? moment(maxDate).format(DATE_TIME_FORMAT.DATE_ONLY)
            : undefined
        }
        className={classNames(
          classes.input,
          hasError ? classes.borderError : classes.borderNormal,
        )}
      />

      {error ? (
        <span className={classes.error}>{error}</span>
      ) : (
        isSubmitted &&
        !value && (
          <span className={classes.error}>
            <FormattedMessage id={defaultErrorMessageId} />
          </span>
        )
      )}
    </Box>
  );
};

export default CustomDate;
